import React, { useEffect, useMemo } from 'react';
import { Space, Typography } from 'antd';
import { renderMarkdownBlocks } from './markdownRenderer.jsx';
import {
  StreamingMarkdownRenderer,
  preloadStreamingMarkdownRenderer,
} from './StreamingMarkdownRenderer.jsx';
import { resolveAssistantMessageErrorDisplay } from './assistantErrorDisplay.js';
import { useSmoothStreamingContent } from './useSmoothStreamingContent.js';
import MessageActionRow from './MessageActionRow.jsx';
import MessageIdentityAvatar from './MessageIdentityAvatar.jsx';

const { Text } = Typography;

const conditionalValue = (condition, whenTrue, whenFalse) => (
  condition ? whenTrue : whenFalse
);
const messageTextStyle = (isUser, themeTokens) => ({
  color: conditionalValue(isUser, themeTokens.textInverse, themeTokens.textPrimary),
  fontSize: 14,
  lineHeight: conditionalValue(isUser, 1.65, 1.75),
});

const canRegenerateMessage = ({ assistantErrorDisplay, isUser, onRegenerate, regenerateEnabled, streaming }) => {
  const canRegenerate = Boolean(assistantErrorDisplay) && !isUser && !streaming && regenerateEnabled && typeof onRegenerate === 'function';
  return canRegenerate;
};

const renderMessageContent = ({
  assistantErrorDisplay,
  createdAt,
  displayContent,
  isUser,
  streaming,
  streamingContent,
  themeTokens,
  citationOptions,
}) => {
  const textStyle = messageTextStyle(isUser, themeTokens);
  if (assistantErrorDisplay) {
    return (
      <Space orientation="vertical" size={6}>
        <Text strong style={{ color: themeTokens.textPrimary, fontSize: 13 }}>
          {assistantErrorDisplay.title}
        </Text>
        <Text style={{ color: themeTokens.textSecondary, fontSize: 12.5 }}>
          {assistantErrorDisplay.userMessage}
        </Text>
      </Space>
    );
  }
  if (streaming) {
    return (
      <StreamingMarkdownRenderer
        content={displayContent}
        pending={streamingContent.pending}
        streaming={streaming}
        textStyle={textStyle}
        citationOptions={citationOptions}
      />
    );
  }
  const roleKey = conditionalValue(isUser, 'user', 'assistant');
  return renderMarkdownBlocks(displayContent, `${roleKey}-${createdAt || 'stream'}`, textStyle, citationOptions);
};

export function MessageBubble(props) {
  const {
    assistantName,
    showIdentity,
    role,
    content,
    createdAt,
    streaming,
    themeTokens,
    onRegenerate,
    regenerateEnabled,
    capabilityActions,
    onCapabilityAction,
    messageIdentityKey,
    renderMessageAvatar,
    citationPayload,
    onUICardAction,
  } = { regenerateEnabled: false, streaming: false, showIdentity: true, ...props };
  const isUser = role === 'user';
  const citationOptions = useMemo(() => ({ citationPayload, onAction: onUICardAction, themeTokens }),
    [citationPayload, onUICardAction, themeTokens]);
  const streamingContent = useSmoothStreamingContent({ content, streaming });
  const displayContent = conditionalValue(streaming, streamingContent.visibleContent, content);
  const assistantErrorDisplay = resolveAssistantMessageErrorDisplay({ role, content: displayContent });
  const copyableContent = [assistantErrorDisplay?.userMessage, displayContent].find(Boolean);
  const canRegenerate = canRegenerateMessage({
    assistantErrorDisplay,
    isUser,
    onRegenerate,
    regenerateEnabled,
    streaming,
  });

  useEffect(() => {
    if (!streaming) {
      return;
    }
    void preloadStreamingMarkdownRenderer().catch(() => {});
  }, [streaming]);

  const messageContentNode = renderMessageContent({
    assistantErrorDisplay,
    createdAt,
    displayContent,
    isUser,
    streaming,
    streamingContent,
    themeTokens,
    citationOptions,
  });
  const messageRoleClass = conditionalValue(isUser, 'is-user', 'is-assistant');
  const identityNode = (
    <MessageIdentityAvatar
      assistantName={assistantName}
      messageIdentityKey={messageIdentityKey}
      renderMessageAvatar={renderMessageAvatar}
      role={role}
      themeTokens={themeTokens}
    />
  );

  return (
    <div
      className={`flare-message-row ${messageRoleClass}`}
    >
      {isUser ? identityNode : null}
      <div
        className="flare-message-column"
      >
        {!isUser && showIdentity ? identityNode : null}
        <div
          className={[
            'flare-message-bubble',
            'flare-message-body',
            conditionalValue(streamingContent.pending, 'is-streaming-pending', ''),
          ].filter(Boolean).join(' ')}
          style={{
            background: conditionalValue(isUser, themeTokens.bubbleUserBg, 'transparent'),
          }}
        >
          <div
            className={[
              conditionalValue(streaming, 'flare-streaming-markdown', ''),
              conditionalValue(streamingContent.pending, 'is-pending', ''),
              conditionalValue(isUser, 'flare-message-markdown-user', 'flare-message-markdown-assistant'),
            ].filter(Boolean).join(' ')}
          >
            {messageContentNode}
          </div>
        </div>
        <MessageActionRow
          capabilityActions={capabilityActions}
          canRegenerate={canRegenerate}
          copyableContent={copyableContent}
          createdAt={createdAt}
          isUser={isUser}
          onCapabilityAction={onCapabilityAction}
          onRegenerate={onRegenerate}
          streaming={streaming}
          themeTokens={themeTokens}
        />
      </div>
    </div>
  );
}
